import React, { useState, useEffect } from 'react';
import LocationForm from './LocationForm';
import { Button } from '../../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';

const LocationsIndex = () => {
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchLocations = async () => {
      try {
        const response = await fetch('/admin/locations', {
          signal: controller.signal
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (isMounted) {
          setLocations(data.locations || []);
          setAreas(data.areas || []);
          setLoading(false);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
          return;
        }
        
        console.error('Error fetching locations:', error);
        
        // Retry once after a delay if component is still mounted
        if (isMounted) {
          setTimeout(() => {
            if (isMounted) {
              fetch('/admin/locations')
                .then(res => res.json())
                .then(data => {
                  if (isMounted) {
                    setLocations(data.locations || []);
                    setAreas(data.areas || []);
                    setLoading(false);
                  }
                })
                .catch(err => {
                  console.error('Retry failed:', err);
                  if (isMounted) {
                    setLoading(false);
                  }
                });
            }
          }, 1000);
        }
      }
    };

    fetchLocations();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const handleCreateLocation = async (locationData) => {
    try {
      const response = await fetch('/admin/locations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ location: locationData })
      });

      if (response.ok) {
        const data = await response.json();
        setLocations([...locations, data.location]);
        setShowForm(false);
      } else {
        const errorData = await response.json();
        alert(errorData.errors.join('\n'));
      }
    } catch (error) {
      console.error('Error creating location:', error);
    }
  };

  const handleUpdateLocation = async (locationData) => {
    try {
      const response = await fetch(`/admin/locations/${editingLocation.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ location: locationData })
      });

      if (response.ok) {
        const data = await response.json();
        setLocations(locations.map(loc => 
          loc.id === data.location.id ? data.location : loc
        ));
        setEditingLocation(null);
      } else {
        const errorData = await response.json();
        alert(errorData.errors.join('\n'));
      }
    } catch (error) {
      console.error('Error updating location:', error);
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!confirm('Are you sure you want to delete this location?')) return;

    try {
      const response = await fetch(`/admin/locations/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        setLocations(locations.filter(loc => loc.id !== id));
      }
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  const filteredLocations = locations.filter(location => {
    const matchesName = location.name.toLowerCase().includes(filterName.toLowerCase());
    const matchesArea = !filterArea || location.primary_area?.id === parseInt(filterArea);
    const matchesActive = filterActive === 'all' || 
      (filterActive === 'active' && location.is_active) ||
      (filterActive === 'inactive' && !location.is_active);
    
    return matchesName && matchesArea && matchesActive;
  });

  // Group locations by area and sort alphabetically
  const groupedLocations = filteredLocations.reduce((groups, location) => {
    const areaName = location.primary_area?.name || 'No Area';
    if (!groups[areaName]) {
      groups[areaName] = [];
    }
    groups[areaName].push(location);
    return groups;
  }, {});

  // Sort locations within each group alphabetically
  Object.keys(groupedLocations).forEach(areaName => {
    groupedLocations[areaName].sort((a, b) => a.name.localeCompare(b.name));
  });

  // Sort area names alphabetically
  const sortedAreaNames = Object.keys(groupedLocations).sort();

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Locations</h1>
        <div className="flex space-x-2">
          <Button onClick={() => setShowForm(true)}>
            New Location
          </Button>
          <Button 
            onClick={() => window.location.href = '/admin/location_boundaries'}
            variant="outline"
          >
            Manage Boundaries
          </Button>
          <Button 
            onClick={() => window.location.href = '/admin/geojson_imports/new'}
            variant="outline"
          >
            Import GeoJSON
          </Button>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Filter by name..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            
            <select
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Areas</option>
              {areas.map(area => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Locations</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {(showForm || editingLocation) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingLocation ? 'Edit Location' : 'New Location'}</CardTitle>
            </CardHeader>
            <CardContent>
              <LocationForm
                location={editingLocation}
                areas={areas}
                onSubmit={editingLocation ? handleUpdateLocation : handleCreateLocation}
                onCancel={() => {
                  setShowForm(false);
                  setEditingLocation(null);
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <div className="space-y-6">
        {sortedAreaNames.map(areaName => (
          <Card key={areaName}>
            <CardHeader className="py-4">
              <CardTitle className="text-lg">
                {areaName} 
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({groupedLocations[areaName].length} location{groupedLocations[areaName].length !== 1 ? 's' : ''})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <colgroup>
                    <col className="w-[25%]" />
                    <col className="w-[25%]" />
                    <col className="w-[15%]" />
                    <col className="w-[8%]" />
                    <col className="w-[10%]" />
                    <col className="w-[7%]" />
                    <col className="w-[10%]" />
                  </colgroup>
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">All Areas</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Offers</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Default</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groupedLocations[areaName].map(location => (
                      <tr key={location.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate">{location.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 truncate">{location.slug}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 truncate">
                          {location.areas.map(area => area.abbreviation).join(', ')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-center">
                          {location.offer_count || 0}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge variant={location.is_active ? 'default' : 'secondary'}>
                            {location.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-center">
                          {location.is_default && (
                            <span className="text-green-600">✓</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-center">
                          <div className="flex justify-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingLocation(location)}
                            >
                              Edit
                            </Button>
                            {location.offer_count === 0 && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteLocation(location.id)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default LocationsIndex;